/**
 * Widget Communication
 * Handles postMessage communication between loader (parent) and widget (iframe)
 */

import type { LoaderToWidget, WidgetToLoader } from "../shared/messages";
import { debug } from "./utils/debug";

export class WidgetCommunication {
  private iframe: HTMLIFrameElement;
  private messageHandlers: Map<string, Function> = new Map();
  private targetOrigin: string;

  constructor(iframe: HTMLIFrameElement) {
    this.iframe = iframe;
    this.targetOrigin = new URL(iframe.src).origin;
    this.setupMessageListener();
  }

  private setupMessageListener(): void {
    window.addEventListener("message", (event) => {
      if (event.source !== this.iframe.contentWindow) {
        return;
      }

      if (event.origin !== this.targetOrigin) {
        debug.warn(
          "[Apten Loader] Ignored message from unauthorized origin:",
          event.origin,
          "Expected origin:",
          this.targetOrigin
        );
        return;
      }

      try {
        const message: WidgetToLoader = event.data;

        // Validate message structure
        if (!message || typeof message.type !== "string") {
          return; // Ignore malformed messages
        }

        const handler = this.messageHandlers.get(message.type);
        if (handler) {
          handler(message.payload);
        } else {
          debug.warn(
            "[Apten Loader] No handler for message type:",
            message.type
          );
        }
      } catch (error) {
        debug.error("[Apten Loader] Error processing message:", error);
      }
    });
  }

  sendToWidget(message: LoaderToWidget): void {
    if (this.iframe.contentWindow) {
      this.iframe.contentWindow.postMessage(message, this.targetOrigin);
    } else {
      debug.warn(
        "[Apten Loader] Cannot send message: iframe contentWindow not available"
      );
    }
  }

  onMessage(type: string, handler: Function): void {
    this.messageHandlers.set(type, handler);
  }

  removeHandler(type: string): void {
    this.messageHandlers.delete(type);
  }

  destroy(): void {
    this.messageHandlers.clear();
    // Note: We don't remove the global message listener here as it might be used by other instances
  }
}
